import React from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, FileCheck2, KeyRound, ShieldCheck } from 'lucide-react';

const providerPlans = [
  {
    id: 'instagram',
    name: 'Instagram',
    summary: 'Create a Meta app and obtain read-only access to a Professional Instagram account.',
    timing: 'Allow 45-90 minutes for an account you own; App Review can take longer.',
    status: 'Business or Creator account required',
    statusClass: 'border-pink-500/30 bg-pink-500/10 text-pink-100',
    haveReady: [
      'The Instagram login and password, with two-factor authentication available.',
      'A Facebook profile with full control of the Facebook Page linked to the Instagram account.',
      'A public privacy-policy URL and data-deletion instructions if the app will be used outside development mode.',
      'A safe password manager where the Meta App Secret and generated tokens can be stored.'
    ],
    steps: [
      {
        title: 'Confirm the Instagram account type',
        body: 'In the Instagram app open Settings and activity, then Account type and tools. If it is Personal, choose Switch to professional account and select Creator or Business. The API cannot read a personal account.'
      },
      {
        title: 'Link the correct Facebook Page',
        body: 'Open the Facebook Page, go to Settings, Permissions, then Linked accounts and connect Instagram. Confirm the Facebook profile doing this has full control of the Page. The Facebook Login route used for these analytics requires this Page-to-Instagram link.'
      },
      {
        title: 'Create the Meta developer app',
        body: 'Sign in to Meta for Developers, open My Apps, choose Create App, select the business use case offered for managing business assets, and give the app a clear name such as Vasia Social Analytics. Keep the app owned by the business rather than an outside supplier.'
      },
      {
        title: 'Add the Instagram product and basic app details',
        body: 'In the app dashboard add the Instagram API product that uses Facebook Login. Complete the app domain, contact email, privacy policy and data-deletion fields. Leave the app in Development mode while testing an account owned by an app-role user.'
      },
      {
        title: 'Add the people and assets used for testing',
        body: 'Under App roles add the Facebook profile as an Administrator, Developer or Tester. Accept the invitation from that Facebook account. Confirm the same person can manage the linked Page and Instagram Professional account.'
      },
      {
        title: 'Request only the read permissions',
        body: 'Use pages_show_list, pages_read_engagement, instagram_basic and instagram_manage_insights. Do not request publishing, comments or messaging permissions for an analytics-only connection. If the app will serve accounts the business does not own, request Advanced Access and complete Meta App Review.'
      },
      {
        title: 'Generate a test user access token',
        body: 'Open Graph API Explorer, select the new app, choose Get User Access Token, tick the four read permissions, and continue through Facebook consent. Copy the resulting token immediately into the password manager; do not send it in chat or email.'
      },
      {
        title: 'Find the Page and Instagram numeric IDs',
        body: 'In Graph API Explorer run GET /me/accounts?fields=id,name,access_token,instagram_business_account. Find the correct Page, then record the id inside instagram_business_account. That is the Instagram Professional Account ID. The Page access token returned for that Page can also be tested against the Instagram endpoints.'
      },
      {
        title: 'Prove the token can read the required data',
        body: 'Run GET /INSTAGRAM_ACCOUNT_ID?fields=username,followers_count,media_count and then GET /INSTAGRAM_ACCOUNT_ID/media?fields=id,caption,media_type,timestamp,permalink. For one returned media ID, test /MEDIA_ID/insights with an available read metric. Fix permissions or Page ownership until all required reads succeed.'
      },
      {
        title: 'Check expiry and prepare for production',
        body: 'Use Meta Access Token Debugger to confirm the app, user, permissions and expiry. For reliable production use, exchange the short-lived token for Meta\'s supported long-lived token and record its expiry. Complete Business Verification and App Review only if Meta requires them for the accounts being connected.'
      }
    ],
    credentials: [
      'Meta App ID and App Secret',
      'Facebook user or Page access token with the approved read permissions',
      'Instagram Professional Account ID (numeric)',
      'Linked Facebook Page ID',
      'Token expiry date and the Graph API version used during testing'
    ],
    checks: [
      'The profile request returns the expected Instagram username.',
      'The media request returns recent posts or Reels.',
      'The insights request succeeds for at least one supported media metric.',
      'Access Token Debugger shows the correct app, user and permissions.'
    ],
    problems: [
      'No instagram_business_account: the Instagram account is personal, linked to a different Page, or the Facebook user lacks full Page control.',
      'Permissions error: regenerate the token after selecting every required read permission and re-authorise the app.',
      'Works for an admin but not another creator: the app probably needs Advanced Access and App Review.'
    ],
    links: [
      ['Meta app dashboard', 'https://developers.facebook.com/apps/'],
      ['Official Instagram setup', 'https://developers.facebook.com/docs/instagram-platform/instagram-api-with-facebook-login/get-started/'],
      ['Graph API Explorer', 'https://developers.facebook.com/tools/explorer/'],
      ['Access Token Debugger', 'https://developers.facebook.com/tools/debug/accesstoken/']
    ]
  },
  {
    id: 'reddit',
    name: 'Reddit',
    summary: 'Request Reddit Data API approval, register an OAuth client, and obtain read-only user tokens.',
    timing: 'Approval time varies. Do not build around credentials until Reddit has approved the use case.',
    status: 'Prior Data API approval required',
    statusClass: 'border-orange-500/30 bg-orange-500/10 text-orange-100',
    haveReady: [
      'The established Reddit account that will own the integration.',
      'A plain-language description of the exact data being read and why it benefits the account owner.',
      'Expected request volume, retention period, security controls and deletion process.',
      'Company/contact details and a public privacy policy for a commercial use case.'
    ],
    steps: [
      {
        title: 'Read the current Reddit policies first',
        body: 'Review the Responsible Builder Policy, Developer Terms and Data API Terms. Reddit now requires explicit approval before Data API access. Do not use scraping, logged-in cookies or unofficial workarounds if approval is not granted.'
      },
      {
        title: 'Write a narrow read-only use case',
        body: 'Describe this as analytics for accounts the creator owns: read the creator\'s submitted posts, score, comment count, link and date. State that the service does not vote, message, post, train AI, resell data or bypass subreddit rules.'
      },
      {
        title: 'Submit the Data Access Request',
        body: 'Open Reddit Help, choose Submit a request, then Data Access Request. Select the commercial or developer category that honestly matches the business. Provide the app name, owner, data fields, OAuth flow, request volume, storage period and deletion method. Save the ticket number.'
      },
      {
        title: 'Wait for written approval or follow-up questions',
        body: 'Reply from the same contact address and keep the use case consistent. Do not submit duplicate applications through other accounts. If Reddit refuses the use case, record Reddit as unavailable and use manual exports instead.'
      },
      {
        title: 'Create the OAuth application after approval',
        body: 'Sign in to the approved Reddit account and open Apps in preferences. Choose create another app, select web app for a server callback, enter the approved app name and description, and enter the exact HTTPS redirect URI. The redirect URI must match during every OAuth request.'
      },
      {
        title: 'Record the client credentials correctly',
        body: 'The short string shown beneath the app name is the Client ID; the field labelled secret is the Client Secret. Store both in a password manager. Never put the Client Secret in frontend code, screenshots or shared documents.'
      },
      {
        title: 'Request the minimum scopes',
        body: 'Use identity, history and read. Request duration=permanent only when a refresh token is required for scheduled syncing. Do not request submit, vote, privatemessages or moderation scopes for read-only analytics.'
      },
      {
        title: 'Complete OAuth 2.0 as the account owner',
        body: 'Send the account owner through Reddit\'s authorisation screen using response_type=code and a random state value. Confirm the returned state matches, then exchange the one-time code server-side at /api/v1/access_token using the Client ID and Client Secret. Store the access and refresh tokens securely.'
      },
      {
        title: 'Test with Reddit\'s OAuth host and a clear User-Agent',
        body: 'Call https://oauth.reddit.com/api/v1/me, then /user/USERNAME/submitted?limit=10 using Authorization: Bearer ACCESS_TOKEN. Send a descriptive User-Agent that identifies the application and contact account. Confirm the returned username and posts are correct.'
      },
      {
        title: 'Plan token renewal and deletion handling',
        body: 'Access tokens are short-lived. Use the refresh token server-side and stop syncing immediately if consent is revoked. Retain only the fields Reddit approved, honour deleted content, and revisit the policy when Reddit changes its terms.'
      }
    ],
    credentials: [
      'Reddit approval or support ticket reference',
      'OAuth Client ID and Client Secret',
      'Exact registered redirect URI',
      'Read-only access token and, when approved, refresh token',
      'Approved Reddit username and descriptive User-Agent value'
    ],
    checks: [
      '/api/v1/me returns the expected Reddit username.',
      'The submitted-posts request returns that user\'s recent posts.',
      'The token contains identity, history and read - no write scopes.',
      'The approved use case and actual stored data still match.'
    ],
    problems: [
      'App creation is blocked: complete the Data Access Request and wait for approval rather than trying more accounts.',
      '401 Unauthorized: check the Bearer header, token expiry and the required descriptive User-Agent.',
      'redirect_uri mismatch: copy the registered URI exactly, including scheme, path and trailing slash.'
    ],
    links: [
      ['Data Access Request', 'https://support.reddithelp.com/hc/en-us/requests/new'],
      ['Responsible Builder Policy', 'https://support.reddithelp.com/hc/en-us/articles/16471395473812-Responsible-Builder-Policy'],
      ['Reddit Data API Wiki', 'https://support.reddithelp.com/hc/en-us/articles/16160319875092-Reddit-Data-API-Wiki'],
      ['OAuth app preferences', 'https://www.reddit.com/prefs/apps']
    ]
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    summary: 'Register a TikTok web app, pass review, and obtain refreshable Login Kit tokens for Display API data.',
    timing: 'TikTok says app review can take several days to two weeks.',
    status: 'App review and OAuth required',
    statusClass: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-100',
    haveReady: [
      'A TikTok for Developers account controlled by the business.',
      'A working HTTPS website plus public Terms of Service and Privacy Policy pages.',
      'An exact HTTPS callback URL with no query string or fragment.',
      'A short screen recording that demonstrates login, consent and the read-only data use.'
    ],
    steps: [
      {
        title: 'Create the developer organisation and app',
        body: 'Sign in to TikTok for Developers, open Manage apps, choose Connect an app, and create a Web application. Use the business name and a clear analytics description. The app starts in Draft mode.'
      },
      {
        title: 'Complete every App details field',
        body: 'Upload the required icon and add the official website, category, description, Terms of Service, Privacy Policy and contact details. The descriptions should explain that the app reads the authorised creator\'s profile and public videos for private analytics.'
      },
      {
        title: 'Verify ownership of the web properties',
        body: 'Use TikTok\'s URL properties controls to verify the website, Terms and Privacy URLs when required. Apps created after September 9, 2024 must verify these properties before production review.'
      },
      {
        title: 'Add Login Kit and the Display API',
        body: 'Under Products choose Add products, add Login Kit, and enable the Display API capability used for profile and video reads. Do not add Content Posting because this connection is analytics-only.'
      },
      {
        title: 'Register the production redirect URI',
        body: 'In Login Kit settings add the exact callback. It must be an absolute HTTPS URL, static, without query parameters or fragments, and match the redirect_uri sent during OAuth. TikTok permits up to ten web redirect URIs.'
      },
      {
        title: 'Request only the necessary scopes',
        body: 'Add user.info.basic for the authorised user\'s display name/avatar and video.list for public video metadata. Additional user fields require their matching approved scopes. Users may approve only a subset, so the callback must record what was actually granted.'
      },
      {
        title: 'Prepare and submit the production review',
        body: 'Implement the login flow in the review build, provide reviewer instructions and a video showing the TikTok consent screen plus the resulting profile/video display. Explain why both scopes are necessary, then submit the app under Production and monitor review comments.'
      },
      {
        title: 'Record the Client Key and Client Secret after approval',
        body: 'Open the approved app\'s credentials and copy the Client Key and Client Secret into a password manager. The secret belongs only on the server. Do not expose it in browser code or a shared guide.'
      },
      {
        title: 'Run the web authorisation flow',
        body: 'Send the creator to https://www.tiktok.com/v2/auth/authorize/ with client_key, response_type=code, scope, redirect_uri and a fresh random state value. On return, reject the response if state does not match. Exchange the code server-side for access_token and refresh_token.'
      },
      {
        title: 'Test profile, videos and token refresh',
        body: 'Call the Display API user-info endpoint and the video-list endpoint with the access token. Confirm the open_id belongs to the intended creator. Store both tokens server-side, record expiry, and test the refresh endpoint before relying on scheduled analytics.'
      }
    ],
    credentials: [
      'Approved TikTok App ID, Client Key and Client Secret',
      'Exact verified website and redirect URI',
      'Approved user.info.basic and video.list scopes',
      'Creator open_id, access token, refresh token and expiry times',
      'Production review status and latest reviewer notes'
    ],
    checks: [
      'The callback rejects an incorrect state value.',
      'User Info returns the expected creator display name/open_id.',
      'Video List returns recent public videos.',
      'A refresh-token test produces a new valid access token.'
    ],
    problems: [
      'redirect_uri error: compare the full registered and requested URLs character by character.',
      'Scope not authorised: confirm it was added to the app, approved in review and granted by the creator.',
      'Review rejected: open the review comments, update the URLs/demo video and resubmit the same app.'
    ],
    links: [
      ['TikTok app dashboard', 'https://developers.tiktok.com/apps/'],
      ['Create an app', 'https://developers.tiktok.com/doc/getting-started-create-an-app'],
      ['Login Kit for Web', 'https://developers.tiktok.com/doc/login-kit-web'],
      ['Display API setup', 'https://developers.tiktok.com/doc/display-api-get-started']
    ]
  },
  {
    id: 'x',
    name: 'X',
    summary: 'Create an X developer app and generate a read-only token for public post metrics.',
    timing: 'Usually 20-45 minutes after the developer account and API plan are active.',
    status: 'Eligible X API plan required',
    statusClass: 'border-blue-500/30 bg-blue-500/10 text-blue-100',
    haveReady: [
      'The X account that will own the developer app and its two-factor authentication.',
      'A short explanation that only public posts and engagement counts will be read.',
      'A suitable X API plan with enough read quota for the intended sync frequency.',
      'A password manager for the Bearer Token and any OAuth secrets.'
    ],
    steps: [
      {
        title: 'Open the X Developer Console',
        body: 'Sign in at console.x.com with the business-owned X account. Complete the developer registration questions truthfully and accept the developer agreement. Record the active API plan and monthly read limits.'
      },
      {
        title: 'Create an app for read-only analytics',
        body: 'Choose Create App, give it a recognisable name such as Vasia Social Analytics, describe the public-post analytics use case, and keep it owned by the business. One app contains its own keys, tokens and settings.'
      },
      {
        title: 'Choose the correct authentication method',
        body: 'For public post metrics, App-only authentication with a Bearer Token is the simplest route. If the integration later needs the creator to authorise private user-context endpoints, enable OAuth 2.0 Authorization Code with PKCE instead. Do not enable write access.'
      },
      {
        title: 'Configure read-only user authentication only if needed',
        body: 'In User authentication settings select read permissions and Web App/Automated App. Register the exact HTTPS callback and website URL. Use tweet.read and users.read; add offline.access only when a refreshable OAuth 2.0 session is genuinely needed.'
      },
      {
        title: 'Generate and save the credentials',
        body: 'Open Keys and tokens. Copy the Bearer Token for App-only reads. If using OAuth 2.0, also record the Client ID and Client Secret. X may show a secret only once; regenerating it invalidates the previous value.'
      },
      {
        title: 'Find the numeric user ID',
        body: 'Use GET https://api.x.com/2/users/by/username/USERNAME with Authorization: Bearer YOUR_TOKEN. Remove the @ from USERNAME. Record data.id from the response; the numeric user ID is different from the handle.'
      },
      {
        title: 'Test the posts endpoint',
        body: 'Call GET https://api.x.com/2/users/USER_ID/tweets with tweet.fields=created_at,public_metrics and a small max_results value. Confirm the response contains the intended account\'s recent original posts and public_metrics.'
      },
      {
        title: 'Confirm what the plan does not provide',
        body: 'Public metrics normally include likes, replies, reposts and quotes. Private impressions, profile visits and some analytics require eligible user-context access and an appropriate paid product. Leave unsupported figures blank rather than treating them as zero.'
      },
      {
        title: 'Set a quota and credential review routine',
        body: 'Check usage in the Developer Console, keep requests inside the plan limits, and record the date the token was last tested. If permissions change, the user must authorise again and old tokens may stop working.'
      }
    ],
    credentials: [
      'X App ID and active API plan',
      'Read-only Bearer Token, or OAuth 2.0 Client ID/Secret when user context is required',
      'Numeric X user ID and public handle',
      'Exact callback URL and approved scopes for an OAuth 2.0 flow',
      'Monthly read quota and the date credentials were last tested'
    ],
    checks: [
      'Username lookup returns the expected handle and numeric user ID.',
      'The user-posts endpoint returns recent posts with public_metrics.',
      'The app has read permission only.',
      'Usage remains below the active plan quota.'
    ],
    problems: [
      '403 or client-not-enrolled: confirm the endpoint is included in the active API plan.',
      '401 Unauthorized: regenerate or recopy the token without spaces and verify which app issued it.',
      'No private impressions: that is a product/access limitation, not a missing public_metrics field.'
    ],
    links: [
      ['X Developer Console', 'https://console.x.com/'],
      ['Official app setup', 'https://docs.x.com/fundamentals/developer-apps'],
      ['Authentication overview', 'https://docs.x.com/fundamentals/authentication/overview'],
      ['User lookup reference', 'https://docs.x.com/x-api/users/lookup/api-reference/get-users-by-username-username']
    ]
  },
  {
    id: 'youtube',
    name: 'YouTube / Shorts',
    summary: 'Create a Google Cloud project and OAuth client authorised to read channel and analytics data.',
    timing: 'Allow 45-90 minutes for testing; Google verification can take longer for external production use.',
    status: 'Google OAuth consent required',
    statusClass: 'border-red-500/30 bg-red-500/10 text-red-100',
    haveReady: [
      'The Google account that owns or manages the YouTube channel.',
      'A business-owned Google Cloud project or permission to create one.',
      'An application name, support email, developer email, privacy policy and homepage.',
      'An exact HTTPS redirect URI for the production OAuth callback.'
    ],
    steps: [
      {
        title: 'Create or select the Google Cloud project',
        body: 'Open Google Cloud Console, use the project selector, and create a project named clearly for Vasia social analytics. Confirm the business controls the billing/contact account and add only the administrators who need access.'
      },
      {
        title: 'Enable both YouTube APIs',
        body: 'Open APIs and Services, Library. Enable YouTube Data API v3 for channel/video metadata and YouTube Analytics API for private channel performance reports. Check Quotas after enabling them.'
      },
      {
        title: 'Configure the Google Auth branding',
        body: 'Open Google Auth Platform or OAuth consent screen. Enter the app name, support email, logo if used, homepage, privacy policy and authorised domains. Add a monitored developer-contact email.'
      },
      {
        title: 'Choose the audience and test users',
        body: 'Choose Internal only if every authorised channel manager belongs to the same eligible Google Workspace organisation; otherwise choose External. While the app is in Testing, add the Google accounts that manage the YouTube channels as test users.'
      },
      {
        title: 'Add only the read scopes',
        body: 'Request https://www.googleapis.com/auth/youtube.readonly and https://www.googleapis.com/auth/yt-analytics.readonly. Do not request upload, manage-account or monetary scopes unless a separate approved requirement exists.'
      },
      {
        title: 'Create the OAuth Client ID',
        body: 'Open Clients or Credentials, choose Create credentials, OAuth client ID, and select Web application. Give it a recognisable name. Add the exact production redirect URI. Add an authorised JavaScript origin only if a browser-based OAuth library genuinely requires it.'
      },
      {
        title: 'Copy the client credentials securely',
        body: 'Save the Client ID and Client Secret in a password manager or server secret store. Download the JSON only to an approved secure location, then remove unnecessary local copies. A normal API key cannot replace OAuth for private YouTube Analytics.'
      },
      {
        title: 'Run OAuth as the channel manager',
        body: 'Send the manager through Google\'s OAuth 2.0 authorisation-code flow with the two read scopes, access_type=offline and consent when a refresh token is required. On the callback, validate state and exchange the code server-side. Select the Google account that actually manages the channel.'
      },
      {
        title: 'Identify the authorised channel',
        body: 'Call YouTube Data API channels.list with part=id,snippet,statistics and mine=true. Record the returned channel ID and confirm the title is Vasia\'s channel. Brand Accounts may require choosing the correct channel during Google sign-in.'
      },
      {
        title: 'Test analytics and refresh',
        body: 'Call YouTube Analytics reports.query with ids=channel==MINE, a short date range, and basic read metrics such as views, estimatedMinutesWatched, likes and subscribersGained. Then test the refresh token. If Google marks the app External, complete the production and verification steps it requests before relying on unattended access.'
      }
    ],
    credentials: [
      'Google Cloud Project ID',
      'OAuth 2.0 Web Client ID and Client Secret',
      'Exact authorised redirect URI',
      'YouTube channel ID and managing Google account',
      'Read-only access token, refresh token and consent/verification status'
    ],
    checks: [
      'channels.list with mine=true returns the intended channel.',
      'A basic YouTube Analytics report returns data for that channel.',
      'The granted scopes are youtube.readonly and yt-analytics.readonly only.',
      'A refresh-token test succeeds before the first scheduled sync.'
    ],
    problems: [
      'No channel returned: sign in with the Google account or Brand Account manager that controls the channel.',
      'redirect_uri_mismatch: copy the exact URI from the error into the Web client configuration.',
      'Access blocked or unverified-app warning: add the account as a test user or complete Google verification for production.'
    ],
    links: [
      ['Google Cloud Console', 'https://console.cloud.google.com/'],
      ['Enable YouTube Data API', 'https://developers.google.com/youtube/v3/getting-started'],
      ['YouTube authentication', 'https://developers.google.com/youtube/documentation/authentication'],
      ['OAuth credentials guide', 'https://developers.google.com/youtube/reporting/guides/registering_an_application']
    ]
  }
];

function Checklist({ items, tone = 'purple' }) {
  const dotClass = tone === 'green' ? 'text-green-300' : tone === 'yellow' ? 'text-yellow-300' : 'text-purple-300';
  return (
    <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-300">
      {items.map((item) => <li key={item} className="flex gap-2"><span className={dotClass}>•</span><span>{item}</span></li>)}
    </ul>
  );
}

function SocialApiPlaybook() {
  return (
    <section id="api-setup-guides" className="overflow-hidden rounded-2xl border border-purple-500/25 bg-gradient-to-br from-purple-950/35 via-gray-900 to-pink-950/20">
      <div className="border-b border-purple-500/15 p-5 sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-pink-300">Step-by-step API handbook</p>
            <h3 className="mt-2 text-2xl font-semibold text-white">Set up each marketing platform from the beginning</h3>
            <p className="mt-3 text-sm leading-6 text-gray-300">
              These instructions cover the provider accounts, approvals, permissions, credentials and provider-side tests. They deliberately stop once the API credentials work; connection controls are kept separate elsewhere on this page.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1.5 text-xs font-medium text-green-200">
            <ShieldCheck size={15} /> Read-only setup
          </span>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-gray-700/80 bg-gray-950/55 p-4">
            <FileCheck2 size={18} className="text-purple-300" />
            <p className="mt-2 text-sm font-semibold text-white">Follow every numbered step</p>
            <p className="mt-1 text-xs leading-5 text-gray-400">Platform review usually fails when ownership, URLs, permissions or test evidence are skipped.</p>
          </div>
          <div className="rounded-xl border border-gray-700/80 bg-gray-950/55 p-4">
            <KeyRound size={18} className="text-pink-300" />
            <p className="mt-2 text-sm font-semibold text-white">Save credentials once</p>
            <p className="mt-1 text-xs leading-5 text-gray-400">Use a password manager. Some providers show a client secret or token only once.</p>
          </div>
          <div className="rounded-xl border border-gray-700/80 bg-gray-950/55 p-4">
            <CheckCircle2 size={18} className="text-green-300" />
            <p className="mt-2 text-sm font-semibold text-white">Test at the provider first</p>
            <p className="mt-1 text-xs leading-5 text-gray-400">Do not call setup complete until the official API returns the correct account and content.</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-500">Provider requirements reviewed against official documentation in August 2026. Portals can change labels without notice.</p>
      </div>

      <div className="divide-y divide-gray-800/80">
        {providerPlans.map((plan, planIndex) => (
          <details key={plan.id} open={planIndex === 0} className="group p-5 sm:p-7">
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-4">
              <div className="max-w-3xl">
                <p className="text-xl font-semibold text-white">{plan.name}</p>
                <p className="mt-1 text-sm leading-6 text-gray-400">{plan.summary}</p>
                <p className="mt-1 text-xs text-gray-500">Expected time: {plan.timing}</p>
              </div>
              <span className={`rounded-full border px-3 py-1.5 text-xs font-medium ${plan.statusClass}`}>{plan.status}</span>
            </summary>

            <div className="mt-6 rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 sm:p-5">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-purple-200">Have these ready before step 1</h4>
              <Checklist items={plan.haveReady} />
            </div>

            <div className="mt-5 space-y-3">
              {plan.steps.map((step, stepIndex) => (
                <article key={step.title} className="rounded-xl border border-gray-800 bg-gray-950/45 p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-purple-400/30 bg-purple-400/10 text-xs font-bold text-purple-200">{stepIndex + 1}</span>
                    <div>
                      <h4 className="font-semibold text-white">{step.title}</h4>
                      <p className="mt-2 text-sm leading-6 text-gray-300">{step.body}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
                <h4 className="text-sm font-semibold text-blue-100">Credential pack to keep</h4>
                <Checklist items={plan.credentials} />
              </div>
              <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
                <h4 className="text-sm font-semibold text-green-100">Provider-side completion checks</h4>
                <Checklist items={plan.checks} tone="green" />
              </div>
              <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-yellow-100"><AlertTriangle size={15} /> Common blockers</h4>
                <Checklist items={plan.problems} tone="yellow" />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {plan.links.map(([label, url]) => (
                <a key={url} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 hover:border-purple-400/50 hover:bg-gray-800">
                  {label} <ExternalLink size={14} />
                </a>
              ))}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

export default SocialApiPlaybook;
